/** True when the node is attached, not `display:none`, and has a non-zero box. */
export function isElementVisible(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (!el.isConnected) return false;
  const style = getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

export function queryVisible(selector: string): Element | null {
  const matches = document.querySelectorAll(selector);
  for (const el of matches) {
    if (isElementVisible(el)) return el;
  }
  return null;
}

export function waitForElement(selector: string, timeoutMs = 8000): Promise<Element> {
  const existing = queryVisible(selector);
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve, reject) => {
    const obs = new MutationObserver(() => {
      const el = queryVisible(selector);
      if (el) {
        obs.disconnect();
        clearTimeout(timer);
        resolve(el);
      }
    });
    obs.observe(document.body, { childList: true, subtree: true, attributes: true });
    const timer = window.setTimeout(() => {
      obs.disconnect();
      reject(new Error(`Tour target not found: ${selector}`));
    }, timeoutMs);
  });
}

/** Wait until any selector matches (forked UI paths). */
export function waitForAnyElement(selectors: readonly string[], timeoutMs = 8000): Promise<Element> {
  const pick = () => {
    for (const sel of selectors) {
      const el = queryVisible(sel);
      if (el) return el;
    }
    return null;
  };

  const existing = pick();
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve, reject) => {
    const obs = new MutationObserver(() => {
      const el = pick();
      if (el) {
        obs.disconnect();
        clearTimeout(timer);
        resolve(el);
      }
    });
    obs.observe(document.body, { childList: true, subtree: true, attributes: true });
    const timer = window.setTimeout(() => {
      obs.disconnect();
      reject(new Error(`Tour target not found: ${selectors.join(' | ')}`));
    }, timeoutMs);
  });
}

export function queryTourTarget(step: { selector?: string; selectors?: readonly string[] }): Element | null {
  if (step.selectors?.length) {
    for (const sel of step.selectors) {
      const el = queryVisible(sel);
      if (el) return el;
    }
    return null;
  }
  if (step.selector) return queryVisible(step.selector);
  return null;
}

export function tourTargetSelector(step: { selector?: string; selectors?: readonly string[] }): string | null {
  const el = queryTourTarget(step);
  if (!el) return step.selector ?? step.selectors?.[0] ?? null;
  if (step.selectors?.length) {
    for (const sel of step.selectors) {
      if (document.querySelector(sel) === el) return sel;
    }
  }
  return step.selector ?? null;
}

export function pathMatchesStep(
  pathname: string,
  href: string,
  opts?: { exact?: boolean },
): boolean {
  if (href === '/') return pathname === '/';
  if (opts?.exact) return pathname === href;
  return pathname === href || pathname.startsWith(`${href}/`);
}
